import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Avatar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import networkService from '../../services/network.service';
import { User } from '../../types';
import TeacherDetailsModal from '../../components/TeacherDetailsModal';

const SearchTeachers: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [teachers, setTeachers] = useState<User[]>([]);
  const [filteredTeachers, setFilteredTeachers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Load teachers
  useEffect(() => {
    const loadTeachers = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await networkService.getTeachers();
        setTeachers(data);
        setFilteredTeachers(data);
      } catch (err) {
        console.error('Error loading teachers:', err);
        setError('Erreur lors du chargement des enseignants');
      } finally {
        setLoading(false);
      }
    };

    loadTeachers();
  }, []);

  // Filter teachers
  useEffect(() => {
    if (!searchQuery) {
      setFilteredTeachers(teachers);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = teachers.filter(
      (teacher) =>
        teacher.first_name?.toLowerCase().includes(query) ||
        teacher.last_name?.toLowerCase().includes(query) ||
        teacher.username?.toLowerCase().includes(query) ||
        teacher.email?.toLowerCase().includes(query)
    );

    setFilteredTeachers(filtered);
  }, [searchQuery, teachers]);

  const handleTeacherClick = (teacherId: number) => {
    setSelectedTeacherId(teacherId);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTeacherId(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Panel - Search Filter */}
        <Grid item xs={12} md={3}>
          <Box sx={{ position: 'sticky', top: 16 }}>
            <TextField
              fullWidth
              label="Rechercher"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Nom, prénom, email..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Grid>

        {/* Right Panel - Results */}
        <Grid item xs={12} md={9}>
          <Box>
            {/* Results count */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filteredTeachers.length} enseignant{filteredTeachers.length > 1 ? 's' : ''} trouvé
              {filteredTeachers.length > 1 ? 's' : ''}
            </Typography>

            {/* Results */}
            <Grid container spacing={2}>
              {filteredTeachers.map((teacher) => (
                <Grid item xs={12} sm={6} md={4} key={teacher.id}>
                  <Card>
                    <CardActionArea onClick={() => handleTeacherClick(teacher.id)}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar
                            src={teacher.profile_photo_url || undefined}
                            sx={{ width: 56, height: 56, mr: 2 }}
                          >
                            <PersonIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                              {teacher.first_name} {teacher.last_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {teacher.username}
                            </Typography>
                          </Box>
                        </Box>

                        {teacher.email && (
                          <Typography variant="body2" color="text.secondary">
                            {teacher.email}
                          </Typography>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {filteredTeachers.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  Aucun enseignant ne correspond à vos critères de recherche
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <TeacherDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        teacherId={selectedTeacherId}
      />
    </Box>
  );
};

export default SearchTeachers;
